import moment, { type Moment } from 'moment';

export type MetaDatePreset =
  | 'today'
  | 'yesterday'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'maximum'
  | 'data_maximum'
  | 'last_3d'
  | 'last_7d'
  | 'last_14d'
  | 'last_28d'
  | 'last_30d'
  | 'last_90d'
  | 'last_week_mon_sun'
  | 'last_week_sun_sat'
  | 'last_quarter'
  | 'last_year'
  | 'this_week_mon_today'
  | 'this_week_sun_today'
  | 'this_year';

export const META_DATE_PRESET_OPTIONS: { value: MetaDatePreset; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'last_3d', label: 'Últimos 3 días' },
  { value: 'last_7d', label: 'Últimos 7 días' },
  { value: 'last_30d', label: 'Últimos 30 días' },
  { value: 'this_month', label: 'Este mes' },
  { value: 'last_month', label: 'Mes anterior' },
  { value: 'this_year', label: 'Este año' },
  { value: 'last_year', label: 'Año anterior' },
  { value: 'maximum', label: 'Máximo (37 meses)' },
];

export const DEFAULT_META_DATE_PRESET: MetaDatePreset = 'last_7d';

/**
 * Mirrors the windows Meta Insights computes server-side for each preset, so the
 * panel's WhatsApp/Lead-stats query (which needs explicit dates) stays aligned with
 * the Meta Ads numbers it's displayed alongside.
 */
export const metaDatePresetToRange = (preset: MetaDatePreset): { start: Moment; end: Moment } => {
  const today = moment().endOf('day');

  switch (preset) {
    case 'today':
      return { start: moment().startOf('day'), end: today };
    case 'yesterday': {
      const y = moment().subtract(1, 'day');
      return { start: y.clone().startOf('day'), end: y.clone().endOf('day') };
    }
    case 'last_3d':
      return { start: moment().subtract(3, 'days').startOf('day'), end: today };
    case 'last_7d':
      return { start: moment().subtract(7, 'days').startOf('day'), end: today };
    case 'last_14d':
      return { start: moment().subtract(14, 'days').startOf('day'), end: today };
    case 'last_28d':
      return { start: moment().subtract(28, 'days').startOf('day'), end: today };
    case 'last_30d':
      return { start: moment().subtract(30, 'days').startOf('day'), end: today };
    case 'last_90d':
      return { start: moment().subtract(90, 'days').startOf('day'), end: today };
    case 'this_month':
      return { start: moment().startOf('month'), end: today };
    case 'last_month': {
      const lm = moment().subtract(1, 'month');
      return { start: lm.clone().startOf('month'), end: lm.clone().endOf('month') };
    }
    case 'this_quarter':
      return { start: moment().startOf('quarter'), end: today };
    case 'last_quarter': {
      const lq = moment().subtract(1, 'quarter');
      return { start: lq.clone().startOf('quarter'), end: lq.clone().endOf('quarter') };
    }
    case 'this_year':
      return { start: moment().startOf('year'), end: today };
    case 'last_year': {
      const ly = moment().subtract(1, 'year');
      return { start: ly.clone().startOf('year'), end: ly.clone().endOf('year') };
    }
    case 'this_week_mon_today':
      return { start: moment().startOf('isoWeek'), end: today };
    case 'this_week_sun_today':
      return { start: moment().startOf('week'), end: today };
    case 'last_week_mon_sun': {
      const lw = moment().subtract(1, 'week');
      return { start: lw.clone().startOf('isoWeek'), end: lw.clone().endOf('isoWeek') };
    }
    case 'last_week_sun_sat': {
      const lw = moment().subtract(1, 'week');
      return { start: lw.clone().startOf('week'), end: lw.clone().endOf('week') };
    }
    case 'maximum':
    case 'data_maximum':
      return { start: moment().subtract(37, 'months').startOf('day'), end: today };
    default:
      return { start: moment().subtract(30, 'days').startOf('day'), end: today };
  }
};

export const getMetaDatePresetLabel = (preset: MetaDatePreset): string => {
  const match = META_DATE_PRESET_OPTIONS.find((opt) => opt.value === preset);
  return match?.label ?? preset;
};

export const isMetaDatePreset = (value: unknown): value is MetaDatePreset =>
  typeof value === 'string' && META_DATE_PRESET_OPTIONS.some((opt) => opt.value === value);
